import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { config } from "./config";
import { getUser, signIn, signOut } from "services/AuthService";

const submissions = [
  { id: "ING-1042", source: "SharePoint", files: 182, status: "Processing" },
  { id: "ING-1038", source: "S3", files: 64, status: "Complete" },
  { id: "ING-1031", source: "Mailbox", files: 27, status: "Complete" },
];

function Shell({ user, onSignOut, children }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/user/dashboard" aria-label="MEA Platform home">
          <span className="brand-mark">M</span>
          <span>MEA Platform</span>
          <span className="poc-chip">POC</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="/user/dashboard">Ingestion</a>
          <a href="/ops/" data-testid="ops-link">MEA Ops</a>
        </nav>
        <div className="identity">
          <span>{user?.attributes?.email || user?.username}</span>
          <button className="quiet" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main>{children}</main>
      <footer>
        <span>Ingestion deployment</span>
        <code data-testid="platform-version">{config.buildVersion}</code>
      </footer>
    </div>
  );
}

function Login({ onAuthenticated }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requestedReturn = params.get("returnTo") || "/user/dashboard";
  const returnTo = requestedReturn.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : "/user/dashboard";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const authenticated = await signIn(username.trim(), password);
      onAuthenticated(authenticated);
      if (returnTo.startsWith("/ops")) window.location.assign(returnTo);
      else navigate(returnTo, { replace: true });
    } catch (authError) {
      setError(authError?.message || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="brand-mark large">M</div>
        <p className="eyebrow">Independent deployment POC</p>
        <h1>Welcome to MEA Platform</h1>
        <p className="muted">One identity and one customer URL across independently deployed modules.</p>
        <form onSubmit={submit}>
          <label>Email<input name="username" type="email" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required /></label>
          <label>Password<input name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        </form>
      </section>
      <section className="login-aside">
        <p className="eyebrow">Architecture proof</p>
        <h2>Two teams. Two releases. One experience.</h2>
        <ul>
          <li>Ingestion owns its routes and release pipeline.</li>
          <li>MEA Ops owns everything below <code>/ops/*</code>.</li>
          <li>Cognito session is reused without another login.</li>
        </ul>
      </section>
    </main>
  );
}

function Dashboard() {
  return (
    <section className="page">
      <p className="eyebrow">Ingestion module</p>
      <h1>Operational overview</h1>
      <p className="lede">This page is owned and deployed by the MEA Platform repository.</p>
      <div className="metrics">
        <article><span>Files today</span><strong>273</strong><small>Across 3 active submissions</small></article>
        <article><span>Successful</span><strong>98.7%</strong><small>Rolling 24 hours</small></article>
        <article><span>Queues healthy</span><strong>6 / 6</strong><small>All consumers reporting</small></article>
      </div>
      <div className="callout">
        <div><p className="eyebrow">Optional module</p><h2>Open MEA Ops without signing in again</h2><p>The URL stays on this host while Amplify delegates the path to a separate deployment.</p></div>
        <a className="button-link" href="/ops/proof" data-testid="open-ops">Open proof page →</a>
      </div>
    </section>
  );
}

function Submissions() {
  return (
    <section className="page">
      <p className="eyebrow">Ingestion-owned route</p>
      <h1>Recent submissions</h1>
      <div className="table-wrap"><table><thead><tr><th>ID</th><th>Source</th><th>Files</th><th>Status</th></tr></thead><tbody>{submissions.map((row) => <tr key={row.id}><td><code>{row.id}</code></td><td>{row.source}</td><td>{row.files}</td><td><span className={`status ${row.status.toLowerCase()}`}>{row.status}</span></td></tr>)}</tbody></table></div>
    </section>
  );
}

export default function App() {
  const [state, setState] = useState({ loading: true, user: null });
  const location = useLocation();

  useEffect(() => {
    getUser().then((user) => setState({ loading: false, user })).catch(() => setState({ loading: false, user: null }));
  }, []);

  useEffect(() => {
    window.__MEA_POC__ = { module: "ingestion", version: config.buildVersion };
  }, []);

  if (state.loading) return <div className="loading">Loading MEA Platform…</div>;
  if (!state.user) {
    if (location.pathname !== "/login") {
      const returnTo = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }
    return <Login onAuthenticated={(user) => setState({ loading: false, user })} />;
  }

  const handleSignOut = async () => {
    await signOut();
    setState({ loading: false, user: null });
  };

  return (
    <Shell user={state.user} onSignOut={handleSignOut}>
      <Routes>
        <Route path="/" element={<Navigate to="/user/dashboard" replace />} />
        <Route path="/login" element={<Navigate to="/user/dashboard" replace />} />
        <Route path="/user/dashboard" element={<Dashboard />} />
        <Route path="/user/submissions" element={<Submissions />} />
        <Route path="*" element={<section className="page"><h1>Page not found</h1><a href="/user/dashboard">Return to Ingestion</a></section>} />
      </Routes>
    </Shell>
  );
}
